import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Save, X, GripVertical, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";

const BLANK = {
  key: "", name: "", tagline: "", price: 0, interval: "month", features: [],
  included_service_ids: [], included_event_ids: [], member_discount_pct: 0,
  highlight: false, cta_label: "Join", cta_to: "", published: true, order: 0,
};

export default function MembershipsPanel() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const { data: memberships = [] } = useQuery({ queryKey: ["admin-memberships"], queryFn: () => api.get("/admin/memberships").then((r) => r.data) });
  const { data: services = [] } = useQuery({ queryKey: ["admin-services-lite"], queryFn: () => api.get("/services").then((r) => r.data) });
  const { data: events = [] } = useQuery({ queryKey: ["admin-events-lite"], queryFn: () => api.get("/events").then((r) => r.data) });

  const save = useMutation({
    mutationFn: (m) => (m.id ? api.put(`/admin/memberships/${m.id}`, m) : api.post("/admin/memberships", m)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-memberships"] }); toast.success("Membership saved"); setEditing(null); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/memberships/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-memberships"] }); toast.success("Membership deleted"); setEditing(null); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const set = (patch) => setEditing((prev) => ({ ...prev, ...patch }));
  const toggleId = (field, id) => set({ [field]: editing[field].includes(id) ? editing[field].filter((x) => x !== id) : [...editing[field], id] });

  return (
    <div data-testid="admin-memberships-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B]">Memberships</h2>
          <p className="text-sm text-[#4A5D6B]">Create tiers, set recurring pricing, and bundle services & events for added value.</p>
        </div>
        <button data-testid="membership-add-btn" onClick={() => setEditing({ ...BLANK, order: memberships.length + 1 })}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#144F75] text-white px-4 py-2 text-sm font-medium hover:bg-[#0f3d5c]">
          <Plus size={16} /> New membership
        </button>
      </div>

      <div className="grid gap-3 mb-8">
        {memberships.map((m) => (
          <div key={m.id} data-testid={`membership-row-${m.key || m.id}`} className="flex items-center justify-between bg-white rounded-xl border border-[#E5E0D8] px-4 py-3">
            <div className="flex items-center gap-3">
              <GripVertical size={16} className="text-[#B8C4CC]" />
              <div>
                <p className="font-medium text-[#1A242B] flex items-center gap-2">{m.name}
                  {m.highlight && <Star size={13} className="text-[#C9A24B] fill-[#C9A24B]" />}
                  {!m.published && <span className="text-xs text-[#B45151] font-normal">(hidden)</span>}
                </p>
                <p className="text-xs text-[#4A5D6B]">{m.interval === "free" || m.price === 0 ? "Free" : `${money(m.price)}/${m.interval}`}
                  {m.member_discount_pct > 0 && ` · ${m.member_discount_pct}% off services`}
                  {(m.included_services?.length || 0) + (m.included_events?.length || 0) > 0 && ` · ${(m.included_services?.length || 0) + (m.included_events?.length || 0)} bundled`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid={`membership-edit-${m.key || m.id}`} onClick={() => setEditing({ ...BLANK, ...m })} className="text-sm text-[#144F75] hover:underline">Edit</button>
              <button data-testid={`membership-delete-${m.key || m.id}`} onClick={() => { if (window.confirm(`Delete ${m.name}?`)) del.mutate(m.id); }} className="text-[#B45151] hover:text-[#8f3f3f]"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="bg-white rounded-2xl border border-[#144F75]/15 shadow-sm p-6" data-testid="membership-editor">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl text-[#1A242B]">{editing.id ? "Edit" : "New"} membership</h3>
            <button onClick={() => setEditing(null)} className="text-[#4A5D6B] hover:text-[#1A242B]"><X size={18} /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Name"><input data-testid="membership-name" value={editing.name} onChange={(e) => set({ name: e.target.value })} className={inp} /></Field>
            <Field label="Key / slug"><input data-testid="membership-key" value={editing.key} onChange={(e) => set({ key: e.target.value })} className={inp} placeholder="community" /></Field>
            <Field label="Tagline" full><input value={editing.tagline} onChange={(e) => set({ tagline: e.target.value })} className={inp} /></Field>
            <Field label="Price (USD)"><input data-testid="membership-price" type="number" step="0.01" value={editing.price} onChange={(e) => set({ price: parseFloat(e.target.value) || 0 })} className={inp} /></Field>
            <Field label="Billing interval">
              <select data-testid="membership-interval" value={editing.interval} onChange={(e) => set({ interval: e.target.value })} className={inp}>
                <option value="free">Free (no billing)</option>
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
              </select>
            </Field>
            <Field label="Member discount on services (%)"><input type="number" value={editing.member_discount_pct} onChange={(e) => set({ member_discount_pct: parseInt(e.target.value) || 0 })} className={inp} /></Field>
            <Field label="Display order"><input type="number" value={editing.order} onChange={(e) => set({ order: parseInt(e.target.value) || 0 })} className={inp} /></Field>
            <Field label="CTA label"><input value={editing.cta_label} onChange={(e) => set({ cta_label: e.target.value })} className={inp} /></Field>
            <Field label="CTA link (for free/external tiers)"><input value={editing.cta_to} onChange={(e) => set({ cta_to: e.target.value })} className={inp} placeholder="/join" /></Field>
          </div>

          <Field label="Features (one per line)" className="mt-4">
            <textarea data-testid="membership-features" value={editing.features.join("\n")} onChange={(e) => set({ features: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} rows={5} className={inp} />
          </Field>

          <div className="grid md:grid-cols-2 gap-6 mt-4">
            <div>
              <p className="text-sm font-semibold text-[#1A242B] mb-2">Bundled wellness services</p>
              <div className="max-h-48 overflow-auto rounded-xl border border-[#E5E0D8] p-2 space-y-1" data-testid="membership-services-picker">
                {services.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm px-2 py-1 rounded-lg hover:bg-[#F9F7F3] cursor-pointer">
                    <input type="checkbox" checked={editing.included_service_ids.includes(s.id)} onChange={() => toggleId("included_service_ids", s.id)} />
                    {s.title}
                  </label>
                ))}
                {services.length === 0 && <p className="text-xs text-[#8FA89B] px-2 py-1">No services yet.</p>}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#1A242B] mb-2">Bundled events</p>
              <div className="max-h-48 overflow-auto rounded-xl border border-[#E5E0D8] p-2 space-y-1" data-testid="membership-events-picker">
                {events.map((ev) => (
                  <label key={ev.id} className="flex items-center gap-2 text-sm px-2 py-1 rounded-lg hover:bg-[#F9F7F3] cursor-pointer">
                    <input type="checkbox" checked={editing.included_event_ids.includes(ev.id)} onChange={() => toggleId("included_event_ids", ev.id)} />
                    {ev.title}
                  </label>
                ))}
                {events.length === 0 && <p className="text-xs text-[#8FA89B] px-2 py-1">No events yet.</p>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6 mt-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.highlight} onChange={(e) => set({ highlight: e.target.checked })} /> Highlight (Most Popular)</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.published} onChange={(e) => set({ published: e.target.checked })} /> Published</label>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setEditing(null)} className="rounded-full px-4 py-2 text-sm border border-[#E5E0D8] text-[#4A5D6B]">Cancel</button>
            <button data-testid="membership-save-btn" disabled={save.isPending || !editing.name} onClick={() => save.mutate(editing)}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#144F75] text-white px-5 py-2 text-sm font-medium hover:bg-[#0f3d5c] disabled:opacity-50">
              <Save size={15} /> Save membership
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full rounded-xl border border-[#E5E0D8] px-3 py-2 text-sm focus:outline-none focus:border-[#144F75]";
const Field = ({ label, children, full, className = "" }) => (
  <div className={`${full ? "md:col-span-2" : ""} ${className}`}>
    <label className="block text-xs uppercase tracking-wider text-[#8FA89B] font-semibold mb-1">{label}</label>
    {children}
  </div>
);
